import React, { useState, useMemo } from 'react';
import { SEO } from '../components/Shared';
import { contentRegistry } from '../contentRegistry';
import { quizBank } from '../quizBank';
import { useAuth } from '../components/AuthContext';
import { getArcticCoachUrl } from '../constants/config';

const AdminDashboard: React.FC = () => {
  const items = Object.values(contentRegistry);
  const categories = ['kyber', 'ai', 'mobile', 'crisis', 'privacy', 'routines', 'cert'];
  const [activeTab, setActiveTab] = useState<'inventory' | 'audit' | 'org' | 'logs'>('inventory');
  const { isOrgMode, setOrgMode } = useAuth();

  // --- BUSINESS LOGIC FOR AUDIT ---
  const TARGET_VERSION = "1.5.0";

  const auditResults = useMemo(() => {
    const brokenLinks: string[] = [];
    const versionDrift: Array<{ slug: string, version: string, drift: boolean }> = [];
    const regressionErrors: string[] = [];

    items.forEach(item => {
      if (item.quizRef && !quizBank[item.quizRef]) {
        brokenLinks.push(`Moduuli "${item.navLabel}" viittaa olemattomaan tenttiin: ${item.quizRef}`);
      }
      const isLegacy = item.version < TARGET_VERSION;
      versionDrift.push({ slug: item.slug, version: item.version, drift: isLegacy });
      if (item.quizRef && quizBank[item.quizRef]) {
        const quiz = quizBank[item.quizRef];
        const sectionIds = new Set(item.sections.map(s => s.id));
        quiz.questions.forEach(q => {
          q.references.forEach(ref => {
            if (!sectionIds.has(ref)) {
              regressionErrors.push(`Tentti "${quiz.title}" (Kysymys: ${q.id}) viittaa olemattomaan osioon: ${ref}`);
            }
          });
        });
      }
    });

    return { brokenLinks, versionDrift, regressionErrors };
  }, [items]);

  const logs = [
    { id: 1, time: '14:20', event: 'Sprint 3 Lock: Integrity Scan Complete', status: 'ok', detail: 'Checked 14 module-quiz pairs. All clear.' },
    { id: 2, time: '14:05', event: 'Master Cert Module Linked', status: 'new', detail: 'Loppukoe ankkuroitu registry-logiikkaan.' },
    { id: 3, time: '13:50', event: 'Terminology Audit', status: 'security', detail: 'Poistettu "Live" viittaukset kriisinhallinnasta.' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <SEO title="Admin Dashboard | Lapland AI Lab" description="Arctic Command Center - Sprint 3 Enterprise Edition" />
      
      <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
        <div className="animate-in fade-in slide-in-from-left duration-700">
          <div className="flex items-center gap-3 mb-4">
             <span className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.5)]"></span>
             <span className="text-emerald-400 font-black tracking-widest uppercase text-[10px]">Sprint 3 / READY TO LOCK</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-black text-white tracking-tighter uppercase leading-[0.8]">
            Arctic <br/><span className="aurora-text">Admin Command</span>
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-3 space-y-8">
          <div className="flex gap-2 p-1.5 glass rounded-2xl w-fit border-slate-800/50 overflow-x-auto no-scrollbar">
            {[
              { id: 'inventory', label: 'Inventaario' },
              { id: 'audit', label: 'Tekninen Audit' },
              { id: 'org', label: 'Org-Asetukset' },
              { id: 'logs', label: 'Loki' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap ${
                  activeTab === tab.id ? 'bg-emerald-500 text-slate-950' : 'text-slate-500 hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'audit' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
               <div className="glass p-10 rounded-[3rem] border-slate-800/50 shadow-2xl">
                 <h3 className="text-2xl font-black text-white mb-10 uppercase tracking-tight">Eheyden tarkistus (Regression)</h3>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="bg-slate-950/50 p-8 rounded-[2rem] border border-slate-800/50">
                       <h4 className="text-red-400 font-black text-[10px] uppercase tracking-widest mb-6">Havaitut Regressiot</h4>
                       {auditResults.regressionErrors.length > 0 ? (
                         <ul className="space-y-3">
                           {auditResults.regressionErrors.map((err, i) => (
                             <li key={i} className="text-[10px] text-red-400 font-mono leading-relaxed bg-red-500/5 p-2 rounded">
                               [REG_ERR]: {err}
                             </li>
                           ))}
                         </ul>
                       ) : (
                         <p className="text-[10px] text-emerald-500 font-mono italic">Kaikki tenttiviitteet kohdistuvat olemassa oleviin sisältö-id:ihin.</p>
                       )}
                    </div>
                    <div className="bg-slate-950/50 p-8 rounded-[2rem] border border-slate-800/50">
                       <h4 className="text-amber-400 font-black text-[10px] uppercase tracking-widest mb-6">Versiohallinta (Drift)</h4>
                       <div className="grid grid-cols-1 gap-2">
                          {auditResults.versionDrift.map(v => (
                            <div key={v.slug} className={`flex justify-between items-center text-[9px] font-mono p-2 rounded ${v.drift ? 'bg-amber-500/10 text-amber-500' : 'bg-emerald-500/10 text-emerald-500'}`}>
                              <span>{v.slug}</span>
                              <span>v{v.version}</span>
                            </div>
                          ))}
                       </div>
                    </div>
                 </div>
               </div>
            </div>
          )}

          {activeTab === 'org' && (
            <div className="glass p-10 rounded-[3rem] border-slate-800/50 shadow-2xl animate-in fade-in slide-in-from-bottom-4">
              <h3 className="text-2xl font-black text-white mb-8 uppercase tracking-tight">Organisaationhallinta</h3>
              <div className="flex items-center justify-between p-8 bg-slate-900/50 rounded-3xl border border-slate-800">
                <div>
                  <h4 className="text-white font-bold mb-1">Pakollinen kirjautuminen (Org Mode)</h4>
                  <p className="text-xs text-slate-500">Kun päällä, kaikki tentit ja progress vaativat tunnistautumisen.</p>
                </div>
                <button onClick={() => setOrgMode(!isOrgMode)} className={`w-14 h-8 rounded-full transition-all relative ${isOrgMode ? 'bg-emerald-500' : 'bg-slate-800'}`}>
                  <div className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-all ${isOrgMode ? 'left-7' : 'left-1'}`}></div>
                </button>
              </div>
            </div>
          )}

          {activeTab === 'inventory' && (
             <div className="glass rounded-[3rem] border-slate-800/50 overflow-hidden shadow-2xl animate-in fade-in slide-in-from-bottom-4">
               <div className="p-10 border-b border-slate-800/50 flex justify-between items-center bg-slate-950/20">
                <h3 className="text-2xl font-black text-white uppercase tracking-tight">Sisältömoduulit</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="bg-slate-900/50 text-slate-600 uppercase text-[10px] font-black tracking-[0.25em]">
                      <th className="px-10 py-6">Otsikko</th>
                      <th className="px-10 py-6">Kategoria</th>
                      <th className="px-10 py-6">Tila</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {items.map(item => (
                      <tr key={item.slug} className="hover:bg-slate-800/20 transition-colors">
                        <td className="px-10 py-6 font-bold text-slate-200">{item.navLabel}</td>
                        <td className="px-10 py-6 uppercase text-[9px] font-black text-slate-500">{item.category}</td>
                        <td className="px-10 py-6 text-[10px] font-black text-emerald-500">v{item.version}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
             </div>
          )}

          {activeTab === 'logs' && (
            <div className="glass p-10 rounded-[3rem] border-slate-800/50 shadow-2xl animate-in fade-in slide-in-from-bottom-4">
               <h3 className="text-2xl font-black text-white mb-10 uppercase tracking-tight">System ChangeLog</h3>
               <div className="space-y-6">
                 {logs.map(log => (
                   <div key={log.id} className="flex gap-6 items-start">
                     <div className="pt-1.5"><div className={`w-2 h-2 rounded-full ${log.status === 'security' ? 'bg-amber-500' : log.status === 'new' ? 'bg-emerald-500' : 'bg-slate-700'}`}></div></div>
                     <div className="flex-grow pb-4 border-b border-slate-800/50">
                       <div className="flex justify-between mb-1">
                         <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">{log.time}</span>
                       </div>
                       <p className="text-sm font-bold text-slate-300">{log.event}</p>
                       <p className="text-[11px] text-slate-500 mt-1">{log.detail}</p>
                     </div>
                   </div>
                 ))}
               </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="glass p-10 rounded-[3rem] border-slate-800/50 shadow-2xl sticky top-32">
            <h3 className="text-xl font-black text-white mb-8 uppercase tracking-tight">Status</h3>
            <div className="space-y-6">
              <div className={`p-4 rounded-2xl border ${isOrgMode ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-slate-900/50 border-slate-800'}`}>
                <span className={`text-[10px] font-black uppercase tracking-widest ${isOrgMode ? 'text-emerald-400' : 'text-slate-500'}`}>Org Mode: {isOrgMode ? 'ON' : 'OFF'}</span>
              </div>

              {/* Arctic Coach Admin Link */}
              <a 
                href={getArcticCoachUrl('admin')} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block p-5 rounded-2xl bg-indigo-500/10 border border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/20 transition-all text-center group"
              >
                <span className="text-[10px] font-black uppercase tracking-widest block mb-1 opacity-60">QA-apuri</span>
                <span className="text-xs font-black">Avaa Arctic Coach (QA-apuri)</span>
              </a>

              <div className="pt-6 border-t border-slate-800">
                <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest block mb-2">Registry Integrity</span>
                <p className={`text-xs font-bold ${auditResults.regressionErrors.length > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                  {auditResults.regressionErrors.length > 0 ? 'Issues Detected' : 'Verified'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
